import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Users,
  Search,
  Eye,
  Check,
  Clock,
  X,
  RefreshCw,
  Phone,
  Mail,
  Building,
  Calendar,
  MessageSquare,
  Trash2,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const statusColors = {
  new: 'bg-blue-500/20 text-blue-400',
  contacted: 'bg-yellow-500/20 text-yellow-400',
  qualified: 'bg-green-500/20 text-green-400',
  closed: 'bg-gray-500/20 text-gray-400',
};

const statusIcons = {
  new: Clock,
  contacted: Phone,
  qualified: Check,
  closed: X,
};

const LeadsPage = () => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedLead, setSelectedLead] = useState(null);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    fetchLeads();
  }, []);

  const fetchLeads = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/leads`);
      setLeads(response.data);
    } catch (error) {
      console.error('Failed to fetch leads:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateLeadStatus = async (leadId, newStatus) => {
    try {
      await axios.patch(`${API}/leads/${leadId}/status`, { status: newStatus });
      setLeads(leads.map(lead => 
        lead.id === leadId ? { ...lead, status: newStatus } : lead
      ));
      if (selectedLead?.id === leadId) {
        setSelectedLead({ ...selectedLead, status: newStatus });
      }
    } catch (error) {
      console.error('Failed to update status:', error);
    }
  };

  const deleteLead = async (leadId) => {
    if (!window.confirm('Are you sure you want to delete this lead?')) return;
    try {
      await axios.delete(`${API}/leads/${leadId}`);
      setLeads(leads.filter(lead => lead.id !== leadId));
      setShowDetails(false);
    } catch (error) {
      console.error('Failed to delete lead:', error);
    }
  };

  const filteredLeads = leads.filter(lead => {
    const matchesSearch = 
      lead.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lead.last_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lead.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lead.company?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || lead.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const stats = {
    total: leads.length,
    new: leads.filter(l => l.status === 'new').length,
    contacted: leads.filter(l => l.status === 'contacted').length,
    qualified: leads.filter(l => l.status === 'qualified').length,
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl text-white font-medium">Leads</h1>
        <p className="text-gray-400">Manage your sales leads</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-[#1a1a1f] rounded-2xl border border-white/5 p-5">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-xl bg-purple-500/20 flex items-center justify-center">
              <Users className="w-5 h-5 text-purple-400" />
            </div>
            <span className="text-gray-400 text-sm">Total Leads</span>
          </div>
          <div className="text-3xl text-white font-bold">{stats.total}</div>
        </div>
        <div className="bg-[#1a1a1f] rounded-2xl border border-white/5 p-5">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-xl bg-blue-500/20 flex items-center justify-center">
              <Clock className="w-5 h-5 text-blue-400" />
            </div>
            <span className="text-gray-400 text-sm">New</span>
          </div>
          <div className="text-3xl text-white font-bold">{stats.new}</div>
        </div>
        <div className="bg-[#1a1a1f] rounded-2xl border border-white/5 p-5">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-xl bg-yellow-500/20 flex items-center justify-center">
              <Phone className="w-5 h-5 text-yellow-400" />
            </div>
            <span className="text-gray-400 text-sm">Contacted</span>
          </div>
          <div className="text-3xl text-white font-bold">{stats.contacted}</div>
        </div>
        <div className="bg-[#1a1a1f] rounded-2xl border border-white/5 p-5">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-xl bg-green-500/20 flex items-center justify-center">
              <Check className="w-5 h-5 text-green-400" />
            </div>
            <span className="text-gray-400 text-sm">Qualified</span>
          </div>
          <div className="text-3xl text-white font-bold">{stats.qualified}</div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
          <input
            type="text"
            placeholder="Search leads..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-12 pr-4 py-3 bg-[#1a1a1f] border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-purple-500/50"
          />
        </div>
        <div className="flex gap-2">
          {['all', 'new', 'contacted', 'qualified', 'closed'].map((status) => (
            <button
              key={status}
              onClick={() => setStatusFilter(status)}
              className={`px-4 py-3 rounded-xl text-sm font-medium transition-all ${
                statusFilter === status
                  ? 'bg-purple-600 text-white'
                  : 'bg-[#1a1a1f] text-gray-400 hover:text-white border border-white/10'
              }`}
            >
              {status.charAt(0).toUpperCase() + status.slice(1)}
            </button>
          ))}
          <button
            onClick={fetchLeads}
            className="px-4 py-3 rounded-xl bg-[#1a1a1f] text-gray-400 hover:text-white border border-white/10 transition-all"
          >
            <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Leads Table */}
      <div className="bg-[#1a1a1f] rounded-2xl border border-white/5 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <RefreshCw className="w-8 h-8 text-purple-500 animate-spin" />
          </div>
        ) : filteredLeads.length === 0 ? (
          <div className="text-center py-20">
            <Users className="w-12 h-12 text-gray-600 mx-auto mb-4" />
            <p className="text-gray-400">No leads found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/5">
                  <th className="text-left text-gray-400 text-sm font-medium px-6 py-4">Name</th>
                  <th className="text-left text-gray-400 text-sm font-medium px-6 py-4">Email</th>
                  <th className="text-left text-gray-400 text-sm font-medium px-6 py-4">Company</th>
                  <th className="text-left text-gray-400 text-sm font-medium px-6 py-4">Status</th>
                  <th className="text-left text-gray-400 text-sm font-medium px-6 py-4">Date</th>
                  <th className="text-right text-gray-400 text-sm font-medium px-6 py-4">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredLeads.map((lead) => {
                  const StatusIcon = statusIcons[lead.status] || Clock;
                  return (
                    <tr 
                      key={lead.id} 
                      className="border-b border-white/5 hover:bg-white/5 transition-colors cursor-pointer"
                      onClick={() => { setSelectedLead(lead); setShowDetails(true); }}
                    >
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-purple-600 flex items-center justify-center text-white font-medium">
                            {lead.first_name?.[0]}{lead.last_name?.[0]}
                          </div>
                          <div>
                            <div className="text-white font-medium">{lead.first_name} {lead.last_name}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-gray-400">{lead.email}</td>
                      <td className="px-6 py-4 text-gray-400">{lead.company}</td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ${statusColors[lead.status]}`}>
                          <StatusIcon className="w-3 h-3" />
                          {lead.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-gray-400 text-sm">
                        {formatDate(lead.created_at)}
                      </td>
                      <td className="px-6 py-4 text-right">
                        <button 
                          onClick={(e) => { e.stopPropagation(); setSelectedLead(lead); setShowDetails(true); }}
                          className="text-gray-400 hover:text-white transition-colors"
                        >
                          <Eye className="w-5 h-5" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Lead Details Modal */}
      {showDetails && selectedLead && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={() => setShowDetails(false)} />
          <div className="relative w-full max-w-lg bg-[#1a1a1f] rounded-3xl border border-white/10 p-8">
            <button
              onClick={() => setShowDetails(false)}
              className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/5 flex items-center justify-center text-gray-400 hover:text-white"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="flex items-center gap-4 mb-6">
              <div className="w-14 h-14 rounded-full bg-purple-600 flex items-center justify-center text-white text-xl font-medium">
                {selectedLead.first_name?.[0]}{selectedLead.last_name?.[0]}
              </div>
              <div>
                <h2 className="text-xl text-white font-medium">{selectedLead.first_name} {selectedLead.last_name}</h2>
                <p className="text-gray-400">{selectedLead.company}</p>
              </div>
            </div>

            <div className="space-y-4 mb-6">
              <div className="flex items-center gap-3 text-gray-400">
                <Mail className="w-5 h-5" />
                <a href={`mailto:${selectedLead.email}`} className="hover:text-white transition-colors">
                  {selectedLead.email}
                </a>
              </div>
              <div className="flex items-center gap-3 text-gray-400">
                <Building className="w-5 h-5" />
                <span>{selectedLead.company}</span>
              </div>
              <div className="flex items-center gap-3 text-gray-400">
                <Calendar className="w-5 h-5" />
                <span>{formatDate(selectedLead.created_at)}</span>
              </div>
            </div>

            {selectedLead.message && (
              <div className="mb-6">
                <div className="flex items-center gap-2 text-gray-400 mb-2">
                  <MessageSquare className="w-4 h-4" />
                  <span className="text-sm">Message</span>
                </div>
                <p className="text-white bg-[#0a0a0b] rounded-xl p-4">{selectedLead.message}</p>
              </div>
            )}

            <div className="mb-6">
              <label className="block text-gray-400 text-sm mb-2">Update Status</label>
              <div className="flex gap-2 flex-wrap">
                {['new', 'contacted', 'qualified', 'closed'].map((status) => (
                  <button
                    key={status}
                    onClick={() => updateLeadStatus(selectedLead.id, status)}
                    className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                      selectedLead.status === status
                        ? 'bg-purple-600 text-white'
                        : 'bg-[#0a0a0b] text-gray-400 hover:text-white border border-white/10'
                    }`}
                  >
                    {status.charAt(0).toUpperCase() + status.slice(1)}
                  </button>
                ))}
              </div>
            </div>

            <button
              onClick={() => deleteLead(selectedLead.id)}
              className="w-full flex items-center justify-center gap-2 bg-red-500/10 hover:bg-red-500/20 text-red-400 py-3 rounded-xl transition-colors"
            >
              <Trash2 className="w-4 h-4" />
              Delete Lead
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeadsPage;
